import * as util from 'util';
import _ from 'lodash';
import {DICTIONARY} from '../../../../../dictionary';
import {AfterViewInit, Component, Input, OnInit, ViewChild} from '@angular/core';
import {GeneralService} from '../../../../../services/generalService';
import {NotificationService} from '../../../../../services/notificationService';

@Component({
    selector: 'new-plan-wizard',
    templateUrl: './new-plan-wizard.component.html',
})
export class NewPlanWizardComponent implements OnInit, AfterViewInit {

    @Input() data;
    @ViewChild('confirmTooltip') confirmTooltip;
    @ViewChild('firstEditTooltip') firstEditTooltip;

    dic = DICTIONARY;
    _ = _;

    popupHead = '';
    popupSubtitle = '';
    confirmLabel = '';
    summaryExpirationDisplay = '';

    activeSection = '';
    completedSections = new Set();
    sectionOrder = [];
    allSectionsCompleted = false;
    availableUsers = 0;
    availableLicenses = 0;

    sections = {
        planManager: 'planManager',
        planDetails: 'planDetails',
        planFeatures: 'planFeatures',
    };

    expandMethods = {
        oneMonth: {display: 'one month', value: 'month'},
        oneYear: {display: 'one year', value: 'year'},
        twoYears: {display: '2 years', value: '2years'},
        threeYears: {display: '3 years', value: '3years'},
    };

    allocatePlanManagedByOptions = {
		you: this.dic.CONSTANTS.planManagedBy.you,
		admin: this.dic.CONSTANTS.planManagedBy.admin,
        template: {display: 'Template', tooltip: 'Plan will be used as a template'}
    };

    selectPlanTemplateTypes = {
        new: 'new',
        duplicate: 'duplicate'
    };

    constructor(public gs: GeneralService, public ns: NotificationService) {}

    ngOnInit() {
        this.initPopupLabels();
        this.initSections();
        this.initWizardData();
    }

    ngAfterViewInit() {
        if (this.data.mode === 'modify' && this.firstEditTooltip) {
            setTimeout(() => {
                this.firstEditTooltip.show();
                document.addEventListener('click', () => {
                    this.firstEditTooltip.hide();
                }, {once: true});
            }, 600);
        }
    }

    initPopupLabels() {
        const userName = this.data.curSharedUser?.name || this.data.curSharedUser?.email || '';
        if (this.data.mode === 'create') {
            this.popupHead = 'Create New Plan';
            this.popupSubtitle = "Fill in the new admin's plan details. We will invite them to sign up.";
            this.confirmLabel = 'Create';
        } else if (this.data.mode === 'allocate') {
            this.popupHead = 'Allocate Admin Plan';
            this.popupSubtitle = `Create a new plan and set ${userName} to be its Admin`;
            this.confirmLabel = 'Allocate';
        } else {
            this.popupHead = 'Modify Admin Plan Resources';
            this.popupSubtitle = `Modify plan resources for ${userName}`;
            this.confirmLabel = 'Update';
        }
    }

    initSections() {
        if (this.data.mode === 'create' || this.data.mode === 'allocate') {
            this.sectionOrder = [this.sections.planManager, this.sections.planDetails, this.sections.planFeatures];
            this.activeSection = this.sections.planManager;
        } else {
            this.sectionOrder = [this.sections.planDetails, this.sections.planFeatures];
            // CC: all sections pre-completed so Edit shows on all collapsed ones in modify
            this.sectionOrder.forEach(section => this.completedSections.add(section));
            this.allSectionsCompleted = true;
        }
    }

    initWizardData() {
        if (this.data.mode === 'create' || this.data.mode === 'allocate') {
            this.data.curSharedUser.planManagedBy = this.dic.CONSTANTS.planManagedBy.you;
            this.data.curSharedUser.adminExpirationDate = new Date();

            const savedSubType = localStorage['newPlanWizardSubType']  || this.dic.CONSTANTS.planSubType.annual;
            this.setMspAnnualAdminValues(savedSubType);
            switch (savedSubType) {
                case this.dic.CONSTANTS.planSubType.poc:
                    this.setExpirationDateForPoc();
                    break;
                case this.dic.CONSTANTS.planSubType.msp:
                    this.setExpirationDateForMsp();
                    break;
                default:
                    this.updateExpirationTime(this.expandMethods.oneYear.value);
            }
        } else {
            this.initExpirationDate();
        }
        this.updateSummaryDisplay();
        this.prepareInvoiceNotifyMessage();
    }

    nextSection(section) {
        if (!this.validateSection(section)) {
            return;
        }
        const isLastSection = this.sectionOrder.indexOf(section) === this.sectionOrder.length - 1;
        const isFirstCompletion = !this.completedSections.has(section);
        this.completedSections.add(section);

        if (section === this.sections.planManager && this.data.curSharedUser.planManagedBy === this.allocatePlanManagedByOptions.template) {
            this.sectionOrder.forEach(sec => this.completedSections.add(sec));
            this.allSectionsCompleted = true;
            this.activeSection = null;
            this.updateSummaryDisplay();
            setTimeout(() => {
                this.confirmTooltip.show();
                document.addEventListener('click', () => {
                    this.confirmTooltip.hide();
                }, {once: true});
            }, 500);
            return;
        }

        this.allSectionsCompleted = this.completedSections.size >= this.sectionOrder.length;
        this.updateSummaryDisplay();
        const currentIndex = this.sectionOrder.indexOf(section);
        if (currentIndex < this.sectionOrder.length - 1) {
            this.activeSection = this.sectionOrder[currentIndex + 1];
        } else {
            this.activeSection = null;
        }
        if (isLastSection && isFirstCompletion) {
            setTimeout(() => {
                this.confirmTooltip.show();
                document.addEventListener('click', () => {
                    this.confirmTooltip.hide();
                }, {once: true});
            }, 500);
        }
    }

    // CC: a section collapses on Next/Back/Edit, so the summary is refreshed on all three and not only on Next
    prevSection(section) {
        const currentIndex = this.sectionOrder.indexOf(section);
        if (currentIndex > 0) {
            this.activeSection = this.sectionOrder[currentIndex - 1];
        }
        this.updateSummaryDisplay();
    }

    editSection(section) {
        this.activeSection = section;
        this.updateSummaryDisplay();
    }

    wizardConfirm() {
        for (const section of this.sectionOrder) {
            if (section !== this.sections.planManager && this.data.curSharedUser.planManagedBy === this.allocatePlanManagedByOptions.template) {
                continue;
            }
            if (!this.validateSection(section)) {
                this.activeSection = section;
                return;
            }
        }
        this.data.curSharedUser.setAsPartner = !!this.data.curSharedUser.customerId;
        this.data.confirmCb();
    }

    validateSection(section) {
        if (section === this.sections.planManager) {
            if (this.data.curSharedUser.planManagedBy === this.dic.CONSTANTS.planManagedBy.admin && !this.data.curSharedUser.email) {
                this.ns.showWarnMessage(util.format(this.dic.ERRORS.emptyField, 'Admin email'));
                return false;
            }
            if (this.data.curSharedUser.planManagedBy === this.dic.CONSTANTS.planManagedBy.admin && !this.gs.validateEmail(this.data.curSharedUser.email)) {
                this.ns.showWarnMessage(util.format(this.dic.ERRORS.invalidEmail, this.data.curSharedUser.email));
                return false;
            }
            if (this.data.curSharedUser.planManagedBy === this.dic.CONSTANTS.planManagedBy.you && !this.data.curSharedUser.domain) {
                this.ns.showWarnMessage(util.format(this.dic.ERRORS.emptyField, 'Domain'));
                return false;
            }
            if (this.data.curSharedUser.planManagedBy === this.dic.CONSTANTS.planManagedBy.you && !this.gs.isValidDomain(this.data.curSharedUser.domain)) {
                this.ns.showWarnMessage(util.format(this.dic.ERRORS.invalidDomain, this.data.curSharedUser.domain));
                return false;
            }
            if (this.data.curSharedUser.planManagedBy === this.allocatePlanManagedByOptions.template && !this.data.curSharedUser.templateName) {
                this.ns.showWarnMessage(util.format(this.dic.ERRORS.emptyField, 'Template name'));
                return false;
            }
        }
        if (section === this.sections.planDetails) {
            if (this.data.curSharedUser.planSubType !== this.dic.CONSTANTS.planSubType.msp && !this.data.curSharedUser.adminExpirationDate) {
                this.ns.showWarnMessage(this.dic.ERRORS.sharedPlanNoExpiration);
                return false;
            }
            if (this.data.copyPolicyData?.enabled && !this.data.copyPolicyData?.selectedPolicyTemplate) {
                this.data.copyPolicyData.error = true;
                this.ns.showErrorMessage(util.format(this.dic.ERRORS.noSelected, 'policy'));
                return false;
            }
        }
        if (section === this.sections.planFeatures) {
            if (!this.data.curSharedUser.allowedUsers) {
                this.ns.showWarnMessage(this.dic.ERRORS.sharedPlanNoUsers);
                return false;
            }
            if (typeof this.data.curSharedUser.allowedLicenses !== 'number' || this.data.curSharedUser.allowedLicenses < 0) {
                this.ns.showWarnMessage(this.dic.ERRORS.sharedPlanNoInboundLicenses);
                return false;
            }
        }
        return true;
    }

    // CC: taken from user-management.component.ts -> formatDateString
    formatDateString(date) {
        if (!date) {
            return;
        }
        const formatDate = new Date(date);
        let formatMonth: any = formatDate.getMonth() + 1;
        let formatDay: any = formatDate.getDate();
        const formatYear = formatDate.getFullYear();
        if (formatMonth < 10) {
            formatMonth = '0' + formatMonth;
        }
        if (formatDay < 10) {
            formatDay = '0' + formatDay;
        }
        return String(formatMonth + '/' + formatDay + '/' + formatYear);
    }

    // CC: taken from user-management.component.ts -> initExpirationDate
    initExpirationDate() {
        if (this.data.curSharedUser?.alloc_plan_id?.expired) {
            this.data.curSharedUser.adminExpirationDate = this.data.curSharedUser.alloc_plan_id.expired;
        }
        this.data.curSharedUser.adminExpirationDateString = this.formatDateString(this.data.curSharedUser.adminExpirationDate);
        if (new Date(this.data.curSharedUser.adminExpirationDate) < new Date()) {
            this.data.curSharedUser.showOldExpirationDate = true;
        }
    }

    // CC: taken from user-management.component.ts -> updateExpirationTime
    updateExpirationTime(expandMethod) {
        this.data.curSharedUser.extendExpirationMethod = expandMethod;
        const currentDate = new Date(this.data.curSharedUser.adminExpirationDate);
        switch (expandMethod) {
            case this.expandMethods.oneMonth.value:
                this.data.curSharedUser.newExpirationDate = new Date(currentDate.setMonth(currentDate.getMonth() + 1));
                break;
            case this.expandMethods.oneYear.value:
                this.data.curSharedUser.newExpirationDate = new Date(currentDate.setFullYear(currentDate.getFullYear() + 1));
                break;
            case this.expandMethods.twoYears.value:
                this.data.curSharedUser.newExpirationDate = new Date(currentDate.setFullYear(currentDate.getFullYear() + 2));
                break;
            case this.expandMethods.threeYears.value:
                this.data.curSharedUser.newExpirationDate = new Date(currentDate.setFullYear(currentDate.getFullYear() + 3));
                break;
        }
    }

    cancelExtendExpirationDate() {
        this.data.curSharedUser.extendExpirationMethod = null;
        this.data.curSharedUser.newExpirationDate = null;
        this.data.curSharedUser.invoiceNotify = null;
        this.prepareInvoiceNotifyMessage();
    }

    setExpirationDateForPoc() {
        const today = new Date();
        let pocDate: any = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 14);
        pocDate = _.min([pocDate, new Date(this.data.adminExpirationDateMaxString)]);
        this.data.curSharedUser.adminExpirationDate = pocDate;
        this.data.curSharedUser.adminExpirationDateString = this.formatDateString(pocDate);
        this.clearExtendExpiration();
    }

    setExpirationDateForMsp() {
        const today = new Date();
        const mspDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
        this.data.curSharedUser.adminExpirationDate = mspDate;
        this.data.curSharedUser.adminExpirationDateString = this.formatDateString(mspDate);
        this.clearExtendExpiration();
    }

    // CC: only Annual can be extended, so a method left over from it would be sent as "extendExpiration"
    // with a PoC/Monthly plan and would keep feeding the summary a stale extended date
    clearExtendExpiration() {
        this.data.curSharedUser.extendExpirationMethod = null;
        this.data.curSharedUser.newExpirationDate = null;
    }

    setMspAnnualAdminValues(planSubType) {
        this.data.curSharedUser.allowedUsers = this.data.curSharedUser.allowedUsers || 1;
        this.data.curSharedUser.allowedLicenses = this.data.curSharedUser.allowedLicenses || 0;
        this.data.curSharedUser.planSubType = planSubType;
    }

    clearPlanAnnual() {
        this.setMspAnnualAdminValues(this.dic.CONSTANTS.planSubType.annual);
        if (this.data.curSharedUser.alloc_plan_id?.expired) {
            this.data.curSharedUser.adminExpirationDate = new Date(this.data.curSharedUser.alloc_plan_id.expired);
        } else {
            this.data.curSharedUser.adminExpirationDate = new Date();
        }
        // CC: in create/allocate an Annual plan defaults to one year. When the form opened on a remembered
        // PoC/Monthly sub type no extend method was set, so fall back to one year instead of leaving today's date
        let extendMethod = this.data.curSharedUser.extendExpirationMethod;
        if (!extendMethod && this.data.mode !== 'modify') {
            extendMethod = this.expandMethods.oneYear.value;
        }
        this.updateExpirationTime(extendMethod);
    }

    clearPlanMsp() {
        this.setMspAnnualAdminValues(this.dic.CONSTANTS.planSubType.msp);
        this.setExpirationDateForMsp();
    }

    changePlanSubType(subType) {
        this.data.curSharedUser.planSubType = subType;
        localStorage['newPlanWizardSubType'] = subType;
        switch (subType) {
            case this.dic.CONSTANTS.planSubType.annual:
                this.clearPlanAnnual();
                break;
            case this.dic.CONSTANTS.planSubType.poc:
                this.setExpirationDateForPoc();
                break;
            case this.dic.CONSTANTS.planSubType.msp:
                this.clearPlanMsp();
                break;
        }
        this.prepareInvoiceNotifyMessage();
    }

    // CC: taken from user-management.component.ts -> extendExpirationDate
    extendExpirationDate(expandMethod) {
        const licenses = Math.max(this.data.curSharedUser.allowedUsers || 0, this.data.curSharedUser.allowedLicenses || 0);
        this.data.curSharedUser.extendExpirationMethod = expandMethod;
        const licensePrice = this.data.sharedPlanInfo?.partners?.license_price;
        switch (expandMethod) {
            case this.expandMethods.oneMonth.value:
                this.data.curSharedUser.invoiceNotify = `Extending plan expiration date by a month, for <b>${licenses}</b> licenses, will cost an additional <b>$${(licenses * licensePrice.monthly).toFixed(2)}</b>.`;
                break;
            case this.expandMethods.oneYear.value:
                this.data.curSharedUser.invoiceNotify = `Extending plan expiration date by a year, for <b>${licenses}</b> licenses, will cost an additional <b>$${(licenses * licensePrice.yearly).toFixed(2)}</b>.`;
                break;
            case this.expandMethods.twoYears.value:
                this.data.curSharedUser.invoiceNotify = `Extending plan expiration date by 2 years, for <b>${licenses}</b> licenses, will cost an additional <b>$${(licenses * 2 * 0.9 * licensePrice.yearly).toFixed(2)}</b>.`;
                break;
            case this.expandMethods.threeYears.value:
                this.data.curSharedUser.invoiceNotify = `Extending plan expiration date by 3 years, for <b>${licenses}</b> licenses, will cost an additional <b>$${(licenses * 3 * 0.8 * licensePrice.yearly).toFixed(2)}</b>.`;
                break;
        }
    }

    // CC: taken from user-management.component.ts -> allocatePlanPartnerInvoiceMessage
    allocatePlanPartnerInvoiceMessage() {
        const newLicenses = Math.max(this.data.curSharedUser.allowedUsers || 0, this.data.curSharedUser.allowedLicenses || 0);
        if (!newLicenses) {
            this.data.curSharedUser.invoiceNotify = null;
            return;
        }
        // @ts-ignore
        const monthsLeft = Math.max(0, Math.ceil((new Date(this.data.curSharedUser.adminExpirationDate) - new Date()) / (30 * 24 * 60 * 60 * 1000))) || 1;
        const monthlyPrice = this.data.sharedPlanInfo?.partners?.license_price?.monthly;
        const unitPrice = monthsLeft <= 12 ? monthlyPrice * monthsLeft :
            monthsLeft <= 24 ? monthlyPrice * monthsLeft * 0.9 :
                monthlyPrice * monthsLeft * 0.8;
        this.data.curSharedUser.invoiceNotify = `Allocate a plan with ${newLicenses} licenses will cost $${newLicenses * unitPrice}`;
    }

    // CC: taken from user-management.component.ts -> addMoreUsersPartnerInvoiceMessage
    addMoreUsersPartnerInvoiceMessage() {
        const currentLicenses = Math.max(
            this.data.curSharedUser.alloc_plan_id.shared_plan.allowed_users,
            this.data.curSharedUser.alloc_plan_id.threat_protection.allowed_users
        );
        const newLicenses = Math.max(this.data.curSharedUser.allowedUsers || 0, this.data.curSharedUser.allowedLicenses || 0);
        if (newLicenses <= currentLicenses) {
            this.data.curSharedUser.invoiceNotify = null;
            return;
        }
        const addUsers = newLicenses - currentLicenses;
        // @ts-ignore
        const monthsLeft = Math.max(0, Math.ceil((new Date(this.data.curSharedUser.alloc_plan_id.expired) - new Date()) / (30 * 24 * 60 * 60 * 1000)));
        if (!monthsLeft) {
            return;
        }
        const monthlyPrice = this.data.sharedPlanInfo?.partners?.license_price?.monthly;
        const unitPrice = monthsLeft <= 12 ? monthlyPrice * monthsLeft :
            monthsLeft <= 24 ? monthlyPrice * monthsLeft * 0.9 :
                monthlyPrice * monthsLeft * 0.8;
        this.data.curSharedUser.invoiceNotify = `Adding these licenses will cost an additional $${addUsers * unitPrice}`;
    }

    // CC: taken from user-management.component.ts -> prepareInvoiceNotifyMessage
    prepareInvoiceNotifyMessage() {
        if (this.data.currentAdminObj) {
            this.availableUsers = this.data.currentAdminObj.alloc_plan_id.shared_plan.allowed_users
                - this.data.currentAdminObj.alloc_plan_id.shared_plan.used_users
                + (this.data.curSharedUser.alloc_plan_id?.shared_plan?.allowed_users || 0);
            this.availableLicenses = this.data.currentAdminObj.alloc_plan_id.threat_protection.allowed_users
                - this.data.currentAdminObj.alloc_plan_id.threat_protection.used_users
                + (this.data.curSharedUser.alloc_plan_id?.threat_protection?.allowed_users || 0);
        }
        if (!this.data.sharedPlanInfo?.partners?.automatic_invoice) {
            this.data.curSharedUser.invoiceNotify = null;
            return;
        }
        if (this.data.curSharedUser.planSubType === this.dic.CONSTANTS.planSubType.poc) {
            this.data.curSharedUser.invoiceNotify = null;
            return;
        }
        if (this.data.curSharedUser.planSubType === this.dic.CONSTANTS.planSubType.msp) {
            this.data.curSharedUser.invoiceNotify = 'An invoice for the allocated Monthly plan will be sent at the start of next month';
            return;
        }
        if (this.data.curSharedUser.extendExpirationMethod) {
            this.extendExpirationDate(this.data.curSharedUser.extendExpirationMethod);
            return;
        }
        if (this.data.mode === 'allocate' || this.data.mode === 'create') {
            this.allocatePlanPartnerInvoiceMessage();
            return;
        }
        this.addMoreUsersPartnerInvoiceMessage();
    }

    updateSummaryDisplay() {
        if (this.data.curSharedUser.planSubType === this.dic.CONSTANTS.planSubType.msp) {
            this.summaryExpirationDisplay = 'Automatically renewed monthly';
            return;
        }
        const expDate = this.data.curSharedUser.newExpirationDate || this.data.curSharedUser.adminExpirationDate;
        this.summaryExpirationDisplay = this.formatDateString(expDate) || '—';
    }
}
